import {
  CreateDateColumn,
  UpdateDateColumn,
  DeleteDateColumn,
  Entity,
  Index,
  Column,
  RelationId,
  ManyToOne,
  PrimaryGeneratedColumn
} from 'typeorm'
import { ObjectType, Field, Int, ID, registerEnumType } from 'type-graphql'

import { Domain, ScalarObject } from '@things-factory/shell'
import { User } from '@things-factory/auth-base'
import { DataSet } from '@things-factory/dataset'
import { KpiPeriodType } from '../kpi/kpi'
import { KpiMetricMeta } from './kpi-metric-meta'

export enum KpiMetricCollectType {
  AUTO = 'AUTO', // 데이터셋 등 자동 수집
  MANUAL = 'MANUAL', // 수동 입력
  IMPORT = 'IMPORT', // 외부 파일 등 임포트
  EXTERNAL = 'EXTERNAL' // 외부 API 등
}
registerEnumType(KpiMetricCollectType, {
  name: 'KpiMetricCollectType',
  description: '방식: AUTO(자동), MANUAL(수동), IMPORT(임포트), EXTERNAL(외부API)'
})

@Entity()
@Index('ix_kpi_metric_0', (kpiMetric: KpiMetric) => [kpiMetric.domain, kpiMetric.name], {
  where: '"deleted_at" IS NULL',
  unique: true
})
@ObjectType({ description: 'KPI metric entity. Represents a source data item used in KPI formulas.' })
export class KpiMetric {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID, { description: 'Unique identifier for this KPI metric.' })
  readonly id: string

  @ManyToOne(type => Domain)
  @Field({ nullable: true, description: 'Domain (tenant) to which this KPI metric belongs.' })
  domain?: Domain

  @RelationId((metric: KpiMetric) => metric.domain)
  @Field({ nullable: true, description: 'ID of the domain (tenant) for this KPI metric.' })
  domainId?: string

  @Column()
  @Field({ description: 'Metric code, unique within the domain, used in KPI formulas.' })
  name: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'User-friendly name or description of the metric.' })
  description?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Unit of measurement for this metric (e.g., %, count, hours).' })
  unit?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Source of the metric data (e.g., system, method, dataset).' })
  source?: string

  @ManyToOne(type => DataSet, { nullable: true })
  @Field(type => DataSet, { nullable: true, description: 'Reference to the source dataset for this metric.' })
  dataSet?: DataSet

  @RelationId((metric: KpiMetric) => metric.dataSet)
  @Field({ nullable: true, description: 'ID of the referenced source dataset.' })
  dataSetId?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Name of the field in the dataset this metric maps to.' })
  fieldName?: string

  @Column({ nullable: false, default: false })
  @Field({ nullable: true, description: 'Indicates whether this metric is active and usable.' })
  active?: boolean

  @Column({ nullable: true })
  @Field({
    nullable: true,
    description: 'Cron schedule string for periodic KPI value aggregation (e.g., "0 0 * * *" for daily).'
  })
  schedule?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Schedule ID for the registered cron job.' })
  scheduleId?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Timezone for the schedule.' })
  timezone?: string

  @Column({ type: 'enum', enum: KpiMetricCollectType, default: KpiMetricCollectType.AUTO })
  @Field(type => KpiMetricCollectType, { description: '데이터 수집 방식' })
  collectType: KpiMetricCollectType

  @Column({ default: 'DAY' })
  @Field(type => KpiPeriodType, { description: 'Aggregation period type for this metric.' })
  periodType: KpiPeriodType

  @Column({ type: 'int', nullable: true })
  @Field(type => Int, { nullable: true, description: '목록 표시 순서(오름차순). 미지정(null)은 뒤로 정렬.' })
  rank?: number

  @Column({ type: 'simple-json', nullable: true })
  @Field(type => ScalarObject, {
    nullable: true,
    description: 'metric 확장 설정(input/kind/source 등). 입력 UI·소스 바인딩 등을 담는 네임스페이스.'
  })
  meta?: KpiMetricMeta

  @CreateDateColumn()
  @Field({ nullable: true, description: 'Timestamp when this KPI metric was created.' })
  createdAt?: Date

  @UpdateDateColumn()
  @Field({ nullable: true, description: 'Timestamp when this KPI metric was last updated.' })
  updatedAt?: Date

  @DeleteDateColumn()
  @Field({ nullable: true, description: 'Timestamp when this KPI metric was deleted (soft delete).' })
  deletedAt?: Date

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'User who created this KPI metric.' })
  creator?: User

  @RelationId((metric: KpiMetric) => metric.creator)
  @Field({ nullable: true, description: 'ID of the user who created this KPI metric.' })
  creatorId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'User who last updated this KPI metric.' })
  updater?: User

  @RelationId((metric: KpiMetric) => metric.updater)
  @Field({ nullable: true, description: 'ID of the user who last updated this KPI metric.' })
  updaterId?: string

  @Field(type => String, { nullable: true, description: 'Thumbnail image or file path for this KPI metric.' })
  thumbnail?: string
}
